// ═══════════════════════════════════════════════════════════════════════════
// APEX TRADING UX ENHANCEMENTS — 7 Components
// File: src/components/TradingEnhancements.jsx
//
// IMPORT IN App.js (add near top with other imports):
// import {
//   MarketCountdown, VixDisplay, DailyGoalTracker,
//   SignalScoreGauge, PositionProgressCard, PnLSparkline,
//   MobileNav, TradingHUD
// } from './components/TradingEnhancements';
//
// USAGE: Add <TradingHUD C={C} /> right after the Trading Mode Bar
//        in your scanner tab. That gives you everything in one block.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Shared time helper ────────────────────────────────────────────────────────
function getET() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
}

// ═══════════════════════════════════════════════════════════════════════════
// 1. MARKET COUNTDOWN — time to open, no-entry window, EOD close
// Usage: <MarketCountdown C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function MarketCountdown({ C }) {
  const [info, setInfo] = useState({});

  useEffect(() => {
    const tick = () => {
      const et    = getET();
      const h     = et.getHours();
      const m     = et.getMinutes();
      const s     = et.getSeconds();
      const mins  = h * 60 + m;
      const day   = et.getDay(); // 0=Sun, 6=Sat
      const isWeekend = day === 0 || day === 6;

      const fmt = (totalSecs) => {
        const hh = Math.floor(totalSecs / 3600);
        const mm = Math.floor((totalSecs % 3600) / 60);
        const ss = totalSecs % 60;
        return hh > 0
          ? `${hh}:${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`
          : `${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
      };

      const secsInDay  = h * 3600 + m * 60 + s;
      const openSecs   = 9 * 3600 + 30 * 60;   // 9:30 AM
      const primeStart = 10 * 3600;             // 10:00 AM
      const noEntry    = 15 * 3600;             // 3:00 PM
      const eodSecs    = 15 * 3600 + 30 * 60;  // 3:30 PM

      if (isWeekend) {
        setInfo({ label: 'MARKET CLOSED', sub: 'Weekend', color: '#757575', phase: 'closed' });
        return;
      }

      if (secsInDay < openSecs) {
        const left = openSecs - secsInDay;
        setInfo({ label: 'MARKET OPENS IN', value: fmt(left), color: '#ffd600', phase: 'pre' });
      } else if (secsInDay < primeStart) {
        const left = primeStart - secsInDay;
        setInfo({ label: 'PRIME WINDOW IN', value: fmt(left), color: '#ffab00', phase: 'open' });
      } else if (secsInDay < noEntry) {
        const left = noEntry - secsInDay;
        setInfo({ label: 'NO-ENTRY WINDOW IN', value: fmt(left), color: '#00c853', phase: 'prime' });
      } else if (secsInDay < eodSecs) {
        const left = eodSecs - secsInDay;
        setInfo({ label: 'EOD FORCE CLOSE IN', value: fmt(left), color: '#ff3d00', phase: 'eod' });
      } else {
        setInfo({ label: 'MARKET CLOSED', sub: 'All positions should be closed', color: '#757575', phase: 'after' });
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  const phaseColors = {
    pre:    { bg: '#ffd60012', border: '#ffd60033' },
    open:   { bg: '#ffab0012', border: '#ffab0033' },
    prime:  { bg: '#00c85312', border: '#00c85333' },
    eod:    { bg: '#ff3d0012', border: '#ff3d0033' },
    after:  { bg: '#75757512', border: '#75757533' },
    closed: { bg: '#75757512', border: '#75757533' },
  };
  const pc = phaseColors[info.phase] || phaseColors.closed;

  return (
    <div style={{
      padding: '8px 14px',
      background: pc.bg,
      border: `1px solid ${pc.border}`,
      borderRadius: 6,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      {/* Phase dot */}
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: info.color,
        boxShadow: info.phase === 'prime' ? `0 0 8px ${info.color}` : 'none',
        animation: info.phase === 'prime' ? 'pulse 1s infinite' : 'none',
        flexShrink: 0,
      }} />
      <div>
        <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1 }}>
          {info.label}
        </div>
        {info.value && (
          <div style={{ fontSize: 20, fontWeight: 700, color: info.color, fontFamily: 'JetBrains Mono', lineHeight: 1.1 }}>
            {info.value}
          </div>
        )}
        {info.sub && (
          <div style={{ fontSize: 10, color: C?.muted, fontFamily: 'JetBrains Mono' }}>{info.sub}</div>
        )}
      </div>
      {/* Phase labels */}
      <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', flexWrap: 'wrap' }}>
        {[
          ['9:30', 'OPEN',  '#ffab00', 'open'],
          ['10:00','PRIME', '#00c853', 'prime'],
          ['3:00', 'STOP',  '#ff9100', 'eod'],
          ['3:30', 'EOD',   '#ff3d00', 'after'],
        ].map(([time, label, col, phase]) => (
          <div key={phase} style={{
            padding: '2px 6px', borderRadius: 3, fontSize: 8,
            fontFamily: 'JetBrains Mono',
            border: `1px solid ${info.phase === phase ? col : (C?.border || '#2a2a2a')}`,
            color: info.phase === phase ? col : (C?.muted || '#757575'),
            background: info.phase === phase ? `${col}15` : 'transparent',
          }}>
            {time} {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 2. VIX DISPLAY — live VIX with color-coded regime
// Usage: <VixDisplay C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function VixDisplay({ C }) {
  const [vix, setVix]     = useState(null);
  const [prev, setPrev]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => {
      fetch('/api/quotes?symbols=%5EVIX')
        .then(r => r.json())
        .then(d => {
          const q = d?.quotes?.quote || d?.quote || d?.[0];
          const val = parseFloat(q?.last || q?.price || q?.close);
          if (!isNaN(val)) {
            setPrev(vix);
            setVix(val);
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const getVixInfo = (v) => {
    if (v === null) return { label: '--',      color: C?.muted || '#757575', bg: '#75757512', desc: 'Loading' };
    if (v < 15)    return { label: 'LOW',      color: C?.green || '#00c853', bg: '#00c85312', desc: 'Calm — ideal for 0DTE' };
    if (v < 20)    return { label: 'NORMAL',   color: C?.accent|| '#f5f5f5', bg: '#f5f5f512', desc: 'Normal — trade full size' };
    if (v < 25)    return { label: 'ELEVATED', color: '#ffab00',              bg: '#ffab0012', desc: 'Elevated — reduce size' };
    if (v < 35)    return { label: 'HIGH',     color: C?.red   || '#ff3d00', bg: '#ff3d0012', desc: 'High — VOLATILE regime' };
    return               { label: 'EXTREME',   color: '#ff00ff',              bg: '#ff00ff12', desc: 'Extreme — avoid 0DTE' };
  };

  const info   = getVixInfo(vix);
  const dir    = prev !== null && vix !== null ? (vix > prev ? '▲' : vix < prev ? '▼' : '—') : '';
  const dirCol = dir === '▲' ? (C?.red || '#ff3d00') : dir === '▼' ? (C?.green || '#00c853') : C?.muted;

  // VIX bar (0-40 scale)
  const barPct = vix ? Math.min(100, (vix / 40) * 100) : 0;

  return (
    <div style={{
      padding: '8px 14px',
      background: info.bg,
      border: `1px solid ${info.color}33`,
      borderRadius: 6, minWidth: 140,
    }}>
      <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1, marginBottom: 4 }}>
        VIX — FEAR INDEX
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: info.color, fontFamily: 'JetBrains Mono', lineHeight: 1 }}>
          {loading ? '--' : vix?.toFixed(2)}
        </div>
        {dir && <div style={{ fontSize: 12, color: dirCol, fontFamily: 'JetBrains Mono' }}>{dir}</div>}
        <div style={{ padding: '2px 6px', borderRadius: 3, fontSize: 8, fontFamily: 'JetBrains Mono', fontWeight: 700, background: `${info.color}22`, color: info.color, marginLeft: 'auto' }}>
          {info.label}
        </div>
      </div>
      {/* Bar */}
      <div style={{ height: 3, background: C?.border, borderRadius: 2, margin: '6px 0 4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${barPct}%`, background: info.color, borderRadius: 2, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ fontSize: 9, color: C?.muted, fontFamily: 'JetBrains Mono' }}>{info.desc}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 3. DAILY GOAL TRACKER — 5-day win rate progress toward go-live threshold
// Usage: <DailyGoalTracker C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function DailyGoalTracker({ C }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const GOAL_WR    = 75;
  const GOAL_DAYS  = 5;
  const GOAL_TRADES = 20;

  useEffect(() => {
    fetch('/api/markov/performance?days=7')
      .then(r => r.json())
      .then(d => { setHistory((d.history || []).reverse()); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Count consecutive days meeting goal
  const qualDays = [...history].reverse().filter((d, i) => {
    if (i >= GOAL_DAYS) return false;
    return d.win_rate >= GOAL_WR && d.total_trades >= GOAL_TRADES;
  }).length;

  const allGood = qualDays >= GOAL_DAYS;

  return (
    <div style={{
      padding: '10px 14px',
      background: allGood ? '#00c85312' : (C?.panel || '#1a1a1a'),
      border: `1px solid ${allGood ? '#00c853' : (C?.border || '#2a2a2a')}`,
      borderRadius: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1 }}>
          GO-LIVE TRACKER — {GOAL_WR}%+ WR × {GOAL_DAYS} DAYS × {GOAL_TRADES}+ TRADES
        </div>
        <div style={{
          padding: '2px 8px', borderRadius: 3, fontSize: 8, fontFamily: 'JetBrains Mono', fontWeight: 700,
          background: allGood ? '#00c85322' : `${C?.border}44`,
          color: allGood ? '#00c853' : C?.muted,
        }}>
          {allGood ? '🚀 READY FOR LIVE' : `${qualDays}/${GOAL_DAYS} DAYS`}
        </div>
      </div>

      {/* Day bubbles */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {Array.from({ length: GOAL_DAYS }).map((_, i) => {
          const day = history[history.length - GOAL_DAYS + i];
          const met = day && day.win_rate >= GOAL_WR && day.total_trades >= GOAL_TRADES;
          const has = !!day;
          return (
            <div key={i} title={day ? `${day.date}: ${day.win_rate?.toFixed(0)}% WR, ${day.total_trades} trades` : 'No data'} style={{
              flex: 1, padding: '6px 0', borderRadius: 4, textAlign: 'center',
              background: !has ? (C?.dim || '#0a0a0a') : met ? '#00c85322' : '#ff3d0022',
              border: `1px solid ${!has ? (C?.border) : met ? '#00c853' : '#ff3d00'}`,
              cursor: 'default',
            }}>
              <div style={{ fontSize: 7, color: C?.muted, fontFamily: 'JetBrains Mono', marginBottom: 2 }}>
                {day ? `D${i+1}` : `D${i+1}`}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, fontFamily: 'JetBrains Mono', color: !has ? C?.muted : met ? '#00c853' : '#ff3d00' }}>
                {!has ? '--' : `${day.win_rate?.toFixed(0)}%`}
              </div>
              <div style={{ fontSize: 7, color: C?.muted, fontFamily: 'JetBrains Mono' }}>
                {!has ? '' : `${day.total_trades}T`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: C?.border, borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 2, transition: 'width 0.5s ease',
          width: `${(qualDays / GOAL_DAYS) * 100}%`,
          background: allGood ? '#00c853' : qualDays >= 3 ? '#ffab00' : '#ff3d00',
        }} />
      </div>
      <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', marginTop: 4 }}>
        {allGood ? 'Threshold met — ready to go live with real capital' :
         `${GOAL_DAYS - qualDays} more qualifying day${GOAL_DAYS - qualDays !== 1 ? 's' : ''} needed`}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 4. SIGNAL SCORE GAUGE — arc gauge replacing raw number
// Usage: <SignalScoreGauge score={87} C={C} size={80} />
// ═══════════════════════════════════════════════════════════════════════════
export function SignalScoreGauge({ score = 0, C, size = 80 }) {
  const r    = size * 0.38;
  const cx   = size / 2;
  const cy   = size / 2;
  const circ = 2 * Math.PI * r;

  // Arc goes from 225° to 315° (270° sweep) — bottom-left to bottom-right
  const sweepDeg  = 240;
  const startDeg  = 150; // bottom-left
  const pct       = Math.min(100, Math.max(0, score)) / 100;
  const filledArc = pct * sweepDeg;

  const polar = (deg) => {
    const rad = (deg - 90) * (Math.PI / 180);
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const arcPath = (startDeg, endDeg, radius) => {
    const s   = polar(startDeg);
    const e   = polar(endDeg);
    const big = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${big} 1 ${e.x} ${e.y}`;
  };

  const col = score >= 90 ? '#00c853' : score >= 80 ? '#00c853' : score >= 75 ? '#ffab00' : score >= 65 ? '#ffd600' : '#ff3d00';

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        {/* Background arc */}
        <path
          d={arcPath(startDeg, startDeg + sweepDeg, r)}
          fill="none" stroke={C?.border || '#2a2a2a'} strokeWidth={size * 0.075}
          strokeLinecap="round"
        />
        {/* Filled arc */}
        {score > 0 && (
          <path
            d={arcPath(startDeg, startDeg + filledArc, r)}
            fill="none" stroke={col} strokeWidth={size * 0.075}
            strokeLinecap="round"
            style={{ filter: score >= 80 ? `drop-shadow(0 0 ${size*0.05}px ${col})` : 'none', transition: 'all 0.5s ease' }}
          />
        )}
        {/* Score text */}
        <text x={cx} y={cy - size * 0.04} textAnchor="middle" fill={col}
          fontSize={size * 0.22} fontWeight="700" fontFamily="JetBrains Mono">
          {score}
        </text>
        <text x={cx} y={cy + size * 0.16} textAnchor="middle" fill={C?.muted || '#757575'}
          fontSize={size * 0.09} fontFamily="JetBrains Mono" letterSpacing="1">
          SCORE
        </text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 5. POSITION PROGRESS CARD — live position with visual T1/T2/T3 bars
// Usage: <PositionProgressCard pos={posObject} currentPrice={cur} C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function PositionProgressCard({ pos, currentPrice, C }) {
  if (!pos) return null;

  const entry = pos.entryPremium;
  const cur   = currentPrice || entry;
  const stop  = entry * 0.65;   // -35%
  const t1    = entry * 1.40;   // +40%
  const t2    = entry * 1.75;   // +75%
  const t3    = entry * 2.50;   // +150%

  const gainPct  = ((cur - entry) / entry) * 100;
  const gainDol  = (cur - entry) * 100 * pos.contracts;
  const isUp     = gainPct >= 0;

  // Map price to position on the bar
  // Bar goes from stop to t3
  const range   = t3 - stop;
  const curPos  = Math.min(100, Math.max(0, ((cur - stop) / range) * 100));
  const entryPos = Math.max(0, ((entry - stop) / range) * 100);
  const t1Pos   = ((t1 - stop) / range) * 100;
  const t2Pos   = ((t2 - stop) / range) * 100;

  const pnlColor = gainPct >= 0 ? (C?.green || '#00c853') : (C?.red || '#ff3d00');

  return (
    <div style={{
      background: C?.panel || '#1a1a1a',
      border: `1px solid ${gainPct > 0 ? (C?.green || '#00c853') : gainPct < -15 ? (C?.red || '#ff3d00') : (C?.border || '#2a2a2a')}33`,
      borderLeft: `3px solid ${pnlColor}`,
      borderRadius: 8, padding: 12, marginBottom: 8,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 13, fontWeight: 700, color: C?.text }}>{pos.ticker}</div>
          <div style={{
            padding: '1px 6px', borderRadius: 3, fontSize: 8, fontFamily: 'JetBrains Mono', fontWeight: 700,
            background: pos.direction === 'BULL' ? `${C?.green}22` : `${C?.red}22`,
            color: pos.direction === 'BULL' ? C?.green : C?.red,
          }}>{pos.direction === 'BULL' ? 'CALL' : 'PUT'}</div>
          {pos.t1Hit && <div style={{ padding: '1px 5px', borderRadius: 3, fontSize: 7, fontFamily: 'JetBrains Mono', background: '#ffab0022', color: '#ffab00' }}>T1 ✓</div>}
          {pos.t2Hit && <div style={{ padding: '1px 5px', borderRadius: 3, fontSize: 7, fontFamily: 'JetBrains Mono', background: '#00c85322', color: '#00c853' }}>T2 ✓</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: pnlColor, fontFamily: 'JetBrains Mono' }}>
            {gainPct >= 0 ? '+' : ''}{gainPct.toFixed(1)}%
          </div>
          <div style={{ fontSize: 10, color: pnlColor, fontFamily: 'JetBrains Mono' }}>
            {gainDol >= 0 ? '+' : ''}${gainDol.toFixed(0)}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ position: 'relative', height: 20, marginBottom: 6 }}>
        {/* Track */}
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 6, background: C?.border, borderRadius: 3, transform: 'translateY(-50%)' }} />

        {/* Filled portion from entry to current */}
        <div style={{
          position: 'absolute', top: '50%', height: 6,
          left: `${entryPos}%`,
          width: `${Math.abs(curPos - entryPos)}%`,
          background: isUp ? (C?.green || '#00c853') : (C?.red || '#ff3d00'),
          borderRadius: 3, transform: 'translateY(-50%)',
          transition: 'width 0.5s ease',
        }} />

        {/* Stop marker */}
        <div style={{ position: 'absolute', left: '0%', top: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 2, height: '100%', background: C?.red || '#ff3d00', opacity: 0.6 }} />
        </div>

        {/* T1 marker */}
        <div style={{ position: 'absolute', left: `${t1Pos}%`, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 2, height: '100%', background: '#ffab00' }} />
        </div>

        {/* T2 marker */}
        <div style={{ position: 'absolute', left: `${t2Pos}%`, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 2, height: '100%', background: C?.green || '#00c853' }} />
        </div>

        {/* Current price dot */}
        <div style={{
          position: 'absolute', top: '50%', left: `${curPos}%`,
          width: 12, height: 12, borderRadius: '50%',
          background: pnlColor, border: `2px solid ${C?.panel || '#1a1a1a'}`,
          transform: 'translate(-50%, -50%)',
          boxShadow: `0 0 6px ${pnlColor}`,
          transition: 'left 0.5s ease',
          zIndex: 2,
        }} />
      </div>

      {/* Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, fontFamily: 'JetBrains Mono', color: C?.muted }}>
        <div style={{ color: C?.red }}>STOP<br/>${stop.toFixed(2)}</div>
        <div style={{ color: C?.text }}>ENTRY<br/>${entry.toFixed(2)}</div>
        <div style={{ color: '#ffab00' }}>T1<br/>${t1.toFixed(2)}</div>
        <div style={{ color: C?.green }}>T2<br/>${t2.toFixed(2)}</div>
        <div style={{ color: C?.green }}>T3<br/>${t3.toFixed(2)}</div>
      </div>

      {/* Extra info */}
      <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 9, fontFamily: 'JetBrains Mono', color: C?.muted }}>
        <div>Contracts: <span style={{ color: C?.text }}>{pos.contracts}</span></div>
        <div>Current: <span style={{ color: C?.text }}>${cur.toFixed(2)}</span></div>
        <div>Score: <span style={{ color: C?.accent }}>{pos.score}</span></div>
        {pos.regimeLabel && <div>Regime: <span style={{ color: pos.regimeLabel === 'TRENDING' ? C?.green : pos.regimeLabel === 'VOLATILE' ? C?.red : '#ffab00' }}>{pos.regimeLabel}</span></div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 6. P&L SPARKLINE — intraday P&L curve
// Usage: <PnLSparkline C={C} width={200} height={50} />
// ═══════════════════════════════════════════════════════════════════════════
export function PnLSparkline({ C, width = 200, height = 50 }) {
  const [points, setPoints] = useState([]);

  useEffect(() => {
    // Load from markov status and add current point every 60s
    const addPoint = () => {
      fetch('/api/markov/journal')
        .then(r => r.json())
        .then(d => {
          const pnl = d?.todaySummary?.total_pnl || 0;
          const time = Date.now();
          setPoints(prev => {
            const next = [...prev, { pnl, time }];
            // Keep last 60 points (60 min at 1/min)
            return next.slice(-60);
          });
        })
        .catch(() => {});
    };
    addPoint();
    const t = setInterval(addPoint, 60000);
    return () => clearInterval(t);
  }, []);

  if (points.length < 2) {
    return (
      <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 9, color: C?.muted, fontFamily: 'JetBrains Mono' }}>Building P&L curve...</div>
      </div>
    );
  }

  const pnls   = points.map(p => p.pnl);
  const minPnl = Math.min(...pnls, 0);
  const maxPnl = Math.max(...pnls, 0);
  const range  = maxPnl - minPnl || 1;
  const pad    = 4;
  const w      = width - pad * 2;
  const h      = height - pad * 2;

  const toX = (i) => pad + (i / (points.length - 1)) * w;
  const toY = (v) => pad + h - ((v - minPnl) / range) * h;
  const zeroY = toY(0);

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(p.pnl)}`).join(' ');
  const fillD = `${pathD} L ${toX(points.length-1)} ${zeroY} L ${toX(0)} ${zeroY} Z`;

  const lastPnl = pnls[pnls.length - 1];
  const isUp    = lastPnl >= 0;
  const col     = isUp ? (C?.green || '#00c853') : (C?.red || '#ff3d00');

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1, marginBottom: 2 }}>
        INTRADAY P&L
      </div>
      <svg width={width} height={height}>
        {/* Zero line */}
        <line x1={pad} y1={zeroY} x2={pad + w} y2={zeroY} stroke={C?.border || '#2a2a2a'} strokeWidth={1} strokeDasharray="3,3" />
        {/* Fill */}
        <path d={fillD} fill={`${col}18`} />
        {/* Line */}
        <path d={pathD} fill="none" stroke={col} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        {/* Last point dot */}
        <circle cx={toX(points.length-1)} cy={toY(lastPnl)} r={3} fill={col} />
      </svg>
      <div style={{ fontSize: 9, color: col, fontFamily: 'JetBrains Mono', fontWeight: 700, textAlign: 'right' }}>
        {lastPnl >= 0 ? '+' : ''}${lastPnl.toFixed(2)}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 7. MOBILE NAV — bottom nav bar for mobile users
// Usage: Add <MobileNav tab={tab} setTab={setTab} C={C} /> at bottom of app
// ═══════════════════════════════════════════════════════════════════════════
export function MobileNav({ tab, setTab, C, tabs = [] }) {
  // Show only on small screens
  const MOBILE_TABS = tabs.length > 0 ? tabs.slice(0, 5) : [
    { id: 'scanner',   label: '📡', name: 'SCAN' },
    { id: 'positions', label: '📊', name: 'POS' },
    { id: 'journal',   label: '📓', name: 'LOG' },
    { id: 'alerts',    label: '🔔', name: 'ALERTS' },
    { id: 'settings',  label: '⚙️', name: 'SET' },
  ];

  return (
    <div style={{
      display: 'none', // Controlled by CSS media query
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: C?.panel || '#1a1a1a',
      borderTop: `1px solid ${C?.border || '#2a2a2a'}`,
      zIndex: 999, paddingBottom: 'env(safe-area-inset-bottom)',
    }} className="mobile-nav">
      {MOBILE_TABS.map(t => (
        <button key={t.id} onClick={() => setTab(t.id)} style={{
          flex: 1, padding: '8px 4px', background: 'none', border: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          borderTop: `2px solid ${tab === t.id ? (C?.accent || '#f5f5f5') : 'transparent'}`,
          cursor: 'pointer',
        }}>
          <span style={{ fontSize: 16 }}>{t.label}</span>
          <span style={{ fontSize: 8, fontFamily: 'JetBrains Mono', color: tab === t.id ? (C?.accent) : C?.muted, letterSpacing: 0.5 }}>{t.name}</span>
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 8. TRADING HUD — master component combining all widgets into one bar
// Add this ONE component to your scanner tab and get everything
// Usage: <TradingHUD C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function TradingHUD({ C }) {
  return (
    <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Row 1: Countdown + VIX + P&L sparkline */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'stretch' }}>
        <MarketCountdown C={C} />
        <VixDisplay C={C} />
        <div style={{
          padding: '8px 14px',
          background: C?.panel || '#1a1a1a',
          border: `1px solid ${C?.border || '#2a2a2a'}`,
          borderRadius: 6,
        }}>
          <PnLSparkline C={C} width={160} height={52} />
        </div>
      </div>

      {/* Row 2: Goal tracker */}
      <DailyGoalTracker C={C} />
    </div>
  );
}
